/**
 * Toast/banner feedback for macro copy actions.
 *
 * Replaces the previous `alert()` UX — alerts steal focus and look out of
 * place on mobile. Inline banner: auto-dismisses after 2.5s, dismissable.
 */

import { useCallback, useEffect, useState } from 'react';

type FeedbackKind = 'success' | 'error';

interface FeedbackState {
    id: number;
    kind: FeedbackKind;
    message: string;
}

export function useMacroFeedback() {
    const [feedback, setFeedback] = useState<FeedbackState | null>(null);

    const show = useCallback((kind: FeedbackKind, message: string) => {
        setFeedback({ id: Date.now() + Math.random(), kind, message });
    }, []);

    const dismiss = useCallback(() => setFeedback(null), []);

    return { feedback, showSuccess: (m: string) => show('success', m), showError: (m: string) => show('error', m), dismiss };
}

export interface MacroFeedbackBannerProps {
    feedback: FeedbackState | null;
    onDismiss: () => void;
}

export function MacroFeedbackBanner({ feedback, onDismiss }: MacroFeedbackBannerProps) {
    useEffect(() => {
        if (!feedback) return;
        const handle = window.setTimeout(onDismiss, 2500);
        return () => window.clearTimeout(handle);
    }, [feedback?.id, onDismiss]);

    if (!feedback) return null;

    const cls =
        feedback.kind === 'error'
            ? 'bg-red-100 text-red-800 border-red-300'
            : 'bg-green-100 text-green-800 border-green-300';

    return (
        <div
            role="status"
            className={`flex items-center justify-between gap-2 rounded border ${cls} px-3 py-2 text-sm`}
        >
            <span>{feedback.message}</span>
            <button
                type="button"
                onClick={onDismiss}
                aria-label="Dismiss"
                className="text-current opacity-60 hover:opacity-100 leading-none"
            >
                &times;
            </button>
        </div>
    );
}
